import { Injectable, OnDestroy, signal, inject } from "@angular/core";
import { type Observable, Subject, Subscription, of } from "rxjs";
import { filter, map, catchError, tap, takeUntil } from "rxjs/operators";
import type { Tables } from "@app/@classes/supabase";
import { AuthStateService } from "@app/@services/shared/auth-state.service";
import { PalmtopScansService } from "@app/@services/data/palmtop-scans.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

// Interfaccia per gli eventi di scansione con topic
interface BarcodeScannedEvent {
	topic?: string | null;
	barcode: string;
}

// Interfaccia per le scansioni salvate in localStorage
export interface SavedScan {
	timestamp: string;
	topic: string | null;
	value: string;
	url?: string; // Opzionale per retrocompatibilità con vecchi record
	syncedAt?: string | null; // Timestamp di quando è stato sincronizzato al database
}

@Injectable({
	providedIn: "root",
})
export class BarcodeScannerService implements OnDestroy {
	// Subject per emettere il codice a barre scansionato con topic
	public barcodeScanned = new Subject<BarcodeScannedEvent>();
	public barcodeScanned$ = this.barcodeScanned.asObservable();

	private buffer: string[] = [];
	private lastKeyDownTime = 0;
	private keydownListener: (event: KeyboardEvent) => void;
	public currentTopic = signal<string | null>(null);
	private readonly STORAGE_KEY = 'barcode_scans_archive';
	private readonly MAX_SCANS = 1000;
	private barcodeScannedSubscription?: Subscription;
	private syncSubscription?: Subscription;
	private syncInterval?: ReturnType<typeof setInterval>;
	private syncInitialized = false; // Protezione per evitare interval multipli
	private readonly authStateService = inject(AuthStateService);
	private readonly palmtopScansService = inject(PalmtopScansService);

	constructor() {
		// Usiamo una funzione freccia per mantenere il corretto contesto di `this`
		this.keydownListener = (event: KeyboardEvent) => this.handleKeyDown(event);
		window.addEventListener("keydown", this.keydownListener);

		// Sottoscrivi al subject per salvare tutte le scansioni in localStorage
		this.barcodeScannedSubscription = this.barcodeScanned$.subscribe((event) => {
			this.saveScanToLocalStorage(event);
		});
	}

	/**
	 * Inizializza la sincronizzazione automatica (chiamato dall'AppComponent)
	 * Protetto contro chiamate multiple
	 */
	public initializeSync(): void {
		if (this.syncInitialized) {
			return;
		}

		// Avvia sincronizzazione automatica ogni 20 secondi (solo sotto /wp/ nel path)
		this.syncInterval = setInterval(() => {
			if (!this.isWarehouseWpPath()) {
				return;
			}
			this.syncScansToDatabase();
		}, 1000 * 20); // 20 secondi

		this.syncInitialized = true;
	}

	/**
	 * Ramo warehouse palmtop nell'SPA: il pathname contiene `/wp/`.
	 */
	private isWarehouseWpPath(): boolean {
		return window.location.pathname.includes("/wp/");
	}

	/**
	 * Imposta il topic corrente per le scansioni
	 * @param topic - Il topic da utilizzare per le scansioni successive
	 */
	public setTopic(topic: string | null): void {
		this.currentTopic.set(topic);
	}

	/**
	 * Ottiene un Observable che emette solo gli eventi di scansione per un topic specifico
	 * @param topic - Il topic da filtrare
	 * @returns Observable che emette solo i codici a barre per il topic specificato
	 */
	public getBarcodeScanned(topic: string): Observable<string> {
		return this.barcodeScanned$.pipe(
			filter((event) => event.topic === topic),
			map((event) => event.barcode.trim()),
		);
	}

	private handleKeyDown(event: KeyboardEvent): void {
		if (!this.isWarehouseWpPath()) {
			return;
		}
		const target = event.target as HTMLElement | null;
		if (!target) {
			return;
		}
		// Non interferire con digitazione in campi testo (textarea serve per a capo con Invio)
		if (
			target.id === "input-scan" ||
			target.tagName === "INPUT" ||
			target.isContentEditable
		) {
			return;
		}
		// Se il tasto premuto è "Enter", la scansione è completa
		if (event.key === "Enter") {
			if (this.buffer.length > 0) {
				const scannedCode = this.buffer.join("");
				// console.log("Barcode scanned:", scannedCode);
				this.barcodeScanned.next({
					topic: this.currentTopic(),
					barcode: scannedCode,
				}); // Emettiamo il codice con topic
				this.buffer = []; // Svuotiamo il buffer
			}
			// Preveniamo l'azione di default (es. submit di un form)
			event.preventDefault();
			return;
		}

		// Ignoriamo tasti di controllo come Shift, Control, etc.
		if (event.key.length > 1) {
			return;
		}

		const currentTime = Date.now();
		// Se è passato troppo tempo dalla pressione precedente, è un utente che digita, non uno scanner.
		// 100ms è un buon valore di partenza, puoi regolarlo.
		if (currentTime - this.lastKeyDownTime > 1000) {
			this.buffer = []; // Reset del buffer
		}

		this.buffer.push(event.key);
		this.lastKeyDownTime = currentTime;
	}

	/**
	 * Salva una scansione in localStorage con logica FIFO (max 1000 elementi)
	 * Non salva duplicati (stesso topic, value e url) a meno di 1 minuto di distanza
	 */
	private saveScanToLocalStorage(event: BarcodeScannedEvent): void {
		try {
			const scans = this.getSavedScans();
			const currentUrl = window.location.href;
			const currentTime = new Date().getTime();

			const newScan: SavedScan = {
				timestamp: new Date().toISOString(),
				topic: event.topic || null,
				value: event.barcode,
				url: currentUrl,
			};

			// Controlla se esiste un record identico negli ultimi 60 secondi
			const isDuplicate = scans.some((scan) => {
				const scanTime = new Date(scan.timestamp).getTime();
				const timeDiff = currentTime - scanTime;

				return (
					scan.topic === newScan.topic &&
					scan.value === newScan.value &&
					scan.url === newScan.url &&
					timeDiff < 60000 // 60 secondi = 1 minuto
				);
			});

			// Non salvare se è un duplicato
			if (isDuplicate) {
				return;
			}

			// Aggiungi la nuova scansione all'inizio
			scans.unshift(newScan);

			// Se superiamo il limite, rimuovi i più vecchi (FIFO)
			if (scans.length > this.MAX_SCANS) {
				scans.splice(this.MAX_SCANS);
			}

			localStorage.setItem(this.STORAGE_KEY, JSON.stringify(scans));
		} catch (error) {
			console.error('Errore nel salvare la scansione in localStorage:', error);
		}
	}

	/**
	 * Recupera tutte le scansioni salvate da localStorage
	 */
	public getSavedScans(): SavedScan[] {
		try {
			const data = localStorage.getItem(this.STORAGE_KEY);
			if (!data) {
				return [];
			}
			return JSON.parse(data) as SavedScan[];
		} catch (error) {
			console.error('Errore nel recuperare le scansioni da localStorage:', error);
			return [];
		}
	}

	/**
	 * Pulisce l'archivio delle scansioni salvate
	 */
	public clearSavedScans(): void {
		try {
			localStorage.removeItem(this.STORAGE_KEY);
		} catch (error) {
			console.error('Errore nel pulire l\'archivio scansioni:', error);
		}
	}

	/**
	 * Sincronizza le scansioni non ancora sincronizzate da localStorage al database
	 */
	private syncScansToDatabase(): void {
		const currentUserId = this.authStateService.currentUserId();
		const currentTenantId = this.authStateService.currentTenantId();

		if (!currentUserId || !currentTenantId) {
			return;
		}

		const scans = this.getSavedScans();
		const unsyncedScans = scans.filter((scan) => !scan.syncedAt);

		if (unsyncedScans.length === 0) {
			return;
		}

		const scansToSync: Partial<Tables<"palmtop_scans">>[] = unsyncedScans.map((scan) => ({
			tenant_id: currentTenantId,
			user_id: currentUserId,
			scan_timestamp: scan.timestamp,
			topic: scan.topic,
			value: scan.value,
			url: scan.url || null,
			synced_at: new Date().toISOString() || null,
		}));

		this.palmtopScansService
			.postManyWithChunk(scansToSync)
			.pipe(
				tap(() => {
					const syncedAt = new Date().toISOString();
					const updatedScans = scans.map((scan) => {
						const wasSynced = unsyncedScans.some(
							(unsyncedScan) =>
								unsyncedScan.timestamp === scan.timestamp &&
								unsyncedScan.value === scan.value &&
								unsyncedScan.topic === scan.topic,
						);
						return wasSynced ? { ...scan, syncedAt } : scan;
					});

					localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedScans));
				}),
				catchError((error) => {
					console.error("SYNC - Errore durante la sincronizzazione:", error);
					return of(null);
				}),
			)
			.subscribe({
				error: (err) => {
					console.error("SYNC - Subscription error:", err);
				},
			});
	}

	ngOnDestroy(): void {
		// Cleanup della subscription
		this.barcodeScannedSubscription?.unsubscribe();
		this.syncSubscription?.unsubscribe();

		// Pulisci l'interval di sincronizzazione
		if (this.syncInterval) {
			clearInterval(this.syncInterval);
			this.syncInterval = undefined;
		}

		// Rimuovi il listener degli eventi keydown
		window.removeEventListener("keydown", this.keydownListener);

		// Complete del subject
		this.barcodeScanned.complete();
	}
}
